import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Btn, Card, Dot, I, Sparkline, StateBadge } from "../ui";
import type { DashboardStats, DeviceDTO, EventDTO } from "../../api/contract";
import { ApiRequestError, getDashboardStats } from "../api";
import { WorkToday } from "./dashboard/WorkToday";
import { VisureQuota } from "./necrologi/VisureQuota";
import {
  ADE_DEFAULT_JOB_RATE_REQ_PER_MIN,
  ADE_SOFT_LIMIT_REQ_PER_MIN,
  aggregateAdeSoftLimitReqPerMin,
} from "../../api/scrape-config";

type Route = string;
type GoFn = (name: Route, args?: Record<string, unknown>) => void;

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 60_000) return "ora";
  if (diff < 60 * 60_000) return `${Math.floor(diff / 60_000)}m fa`;
  if (diff < 24 * 60 * 60_000) return `${Math.floor(diff / (60 * 60_000))}h fa`;
  return `${Math.floor(diff / (24 * 60 * 60_000))}g fa`;
}

function deviceStateKey(d: DeviceDTO): "online" | "offline" | "revoked" {
  if (d.revokedAt) return "revoked";
  if (d.online) return "online";
  return "offline";
}

export function Dashboard({
  devices,
  liveEvents,
  spark,
  onGo,
  memberView = false,
}: {
  devices: DeviceDTO[];
  liveEvents: EventDTO[];
  spark: number[];
  onGo: GoFn;
  memberView?: boolean;
}) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsErr, setStatsErr] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    setStatsErr(null);
    try {
      setStats(await getDashboardStats());
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : "Errore caricamento metriche";
      setStatsErr(msg);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const counts = useMemo(() => {
    const online = devices.filter((d) => d.online && !d.revokedAt).length;
    const offline = devices.filter((d) => !d.online && !d.revokedAt).length;
    return { online, offline, active: online + offline };
  }, [devices]);

  // Le estensioni revocate restano nello storico ma non sono più operative.
  const activeDevices = devices.filter((d) => !d.revokedAt);
  const lastSpark = spark.length ? spark[spark.length - 1]! : 0;
  const avgSpark = spark.length ? Math.round(spark.reduce((a, b) => a + b, 0) / spark.length) : 0;
  const aggregateLimit = aggregateAdeSoftLimitReqPerMin(counts.online);

  return (
    <div className="col gap-16">
      <WorkToday onGo={onGo} onlineCount={counts.online} />
      <VisureQuota />

      <h2 className="dashboard-section-title">Stato del sistema</h2>
      {statsErr && <div className="muted" style={{ fontSize: 12 }}>{statsErr}</div>}

      <div className="grid dashboard-main-grid">
        <Card
          title={memberView ? "Attività SISTER" : "Richieste a SISTER al minuto"}
          sub={memberView ? "Ricerche aggiornate negli ultimi 60 minuti" : `ultimi 60 minuti · ritmo normale ${ADE_DEFAULT_JOB_RATE_REQ_PER_MIN}/min, limite ${ADE_SOFT_LIMIT_REQ_PER_MIN}/min per estensione`}
          actions={<Badge kind="muted">finestra 1h</Badge>}
        >
          <div style={{ height: 200, position: "relative" }}>
            <div style={{ position: "absolute", top: 0, right: 0, zIndex: 2 }} className="col gap-4">
              <div className="row gap-6" style={{ alignItems: "center" }}>
                <Dot kind="live" />
                <span className="mono" style={{ fontSize: 11 }}>{lastSpark} {memberView ? "ricerche/min" : "al minuto"}</span>
              </div>
              <div className="muted mono" style={{ fontSize: 10 }}>{memberView ? `media ${avgSpark}` : `limite ${aggregateLimit} · media ${avgSpark}`}</div>
            </div>
            <div style={{ height: "100%" }}>
              <Sparkline points={spark} height={200} color="var(--accent)" />
            </div>
          </div>
        </Card>

        <Card
          title="Attività recente"
          sub="scraping delle fonti e ricerche"
          actions={<Btn size="sm" variant="ghost" icon={I.refresh} onClick={() => void loadStats()}>Aggiorna</Btn>}
          noPad
        >
          <div className="ev-list" style={{ maxHeight: 260, overflowY: "auto" }}>
            {liveEvents.length === 0 && (
              <div style={{ padding: 24, color: "var(--text-2)", textAlign: "center", fontSize: 13 }}>Nessuna attività recente</div>
            )}
            {liveEvents.slice(0, 8).map((e) => (
              <div className="ev" key={e.id}>
                <div className="ev-time">{formatEventTime(e.t)}</div>
                <div className={`ev-icon ${e.kind}`}>{e.kind === "ok" ? "✓" : e.kind === "warn" ? "!" : e.kind === "err" ? "×" : "i"}</div>
                <div className="ev-body">
                  <div className="ev-title">{e.title}</div>
                  <div className="ev-meta">{e.meta ?? ""}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card
        title="Estensioni abbinate"
        sub={`${counts.active} attive · ${counts.online} accese adesso`}
        actions={<Btn size="sm" variant="ghost" onClick={() => onGo("extension")}>Gestisci →</Btn>}
        noPad
      >
        <div>
          {activeDevices.length === 0 && (
            <div style={{ padding: 24, color: "var(--text-2)", textAlign: "center", fontSize: 13 }}>
              Nessuna estensione abbinata. Vai su Estensione Chrome per abbinarne una.
            </div>
          )}
          {activeDevices.slice(0, 5).map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: "1px solid var(--line)" }}>
              <div className="avatar" style={{ width: 26, height: 26, fontSize: 10 }}>{d.label.substring(0, 2).toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{d.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-2)" }}>ultimo accesso {formatRelative(d.lastSeenAt)}</div>
              </div>
              <span style={{ fontSize: 12, color: "var(--text-2)" }}>{d.tasksDone} ricerche</span>
              <StateBadge state={deviceStateKey(d)} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function formatEventTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}
